"use client"
import { useEffect, useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Save, KeyRound, Link2 } from "lucide-react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import toast from "react-hot-toast"

const DEFAULT_CONFIG = {
  enabled: false,
  authRequired: true,
  authHeaderName: "x-api-key",
  apiKeyLast4: "",
  webhookUrl: "",
  provider: "realtime",
}

export default function BiometricIntegrationSettings() {
  const [schoolId, setSchoolId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const [apiKey, setApiKey] = useState("")
  const [apiKeyLast4, setApiKeyLast4] = useState("")
  const [webhookUrl, setWebhookUrl] = useState("")

  const maskedKey = useMemo(() => {
    if (!apiKeyLast4) return "Not configured"
    return `********${apiKeyLast4}`
  }, [apiKeyLast4])

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      setError("")

      const userStr = localStorage.getItem("user")
      const user = userStr ? JSON.parse(userStr) : null
      const resolvedSchoolId =
        user?.school && (typeof user.school === "object" ? user.school._id : user.school)

      if (!resolvedSchoolId) {
        setError("School context not found")
        return
      }

      setSchoolId(resolvedSchoolId)

      const res = await axiosInstance.get(API_ENDPOINTS.schoolFeatures(resolvedSchoolId))
      const biometric = {
        ...DEFAULT_CONFIG,
        ...(res.data?.data?.biometricIntegration || {}),
      }

      setApiKeyLast4(String(biometric.apiKeyLast4 || ""))
      setWebhookUrl(String(biometric.webhookUrl || ""))
    } catch (err) {
      console.error("Error loading biometric settings:", err)
      setError(err.response?.data?.message || "Failed to load biometric settings")
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!schoolId) {
      toast.error("School context missing")
      return
    }

    if (webhookUrl && !/^https?:\/\//i.test(webhookUrl.trim())) {
      toast.error("Webhook URL must start with http:// or https://")
      return
    }

    try {
      setSaving(true)
      setError("")

      const payload = {
        biometricIntegration: {
          enabled: true,
          authRequired: true,
          authHeaderName: "x-api-key",
          webhookUrl: webhookUrl.trim(),
          provider: "realtime",
        },
      }

      if (apiKey.trim()) {
        payload.biometricApiKey = apiKey.trim()
      }

      const res = await axiosInstance.patch(API_ENDPOINTS.schoolFeatures(schoolId), payload)
      const saved = {
        ...DEFAULT_CONFIG,
        ...(res.data?.data?.biometricIntegration || {}),
      }

      setApiKeyLast4(String(saved.apiKeyLast4 || ""))
      setWebhookUrl(String(saved.webhookUrl || ""))
      setApiKey("")

      toast.success("Biometric integration settings saved")
    } catch (err) {
      console.error("Error saving biometric settings:", err)
      const message = err.response?.data?.message || "Failed to save biometric settings"
      setError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-2xl">Biometric Integration</CardTitle>
          <CardDescription>
            Configure school-specific biometric API key and webhook URL.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="biometric-api-key" className="text-base font-semibold flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              API Key
            </Label>
            <Input
              id="biometric-api-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter a new key to rotate"
            />
            <p className="text-xs text-gray-500">Current key: {maskedKey}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="biometric-webhook-url" className="text-base font-semibold flex items-center gap-2">
              <Link2 className="h-4 w-4" />
              Vendor Webhook URL
            </Label>
            <Input
              id="biometric-webhook-url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://vendor.example.com/webhook"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0263cc] text-white hover:bg-[#0263cc]/90 min-w-[140px]"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
